'use client';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

type QuitReasonModalProps = {
  open: boolean;
  title: string;
  question: string;
  reasons: Record<string, string>;
  otherPlaceholder: string;
  stayLabel: string;
  closeLabel: string;
  onCancel: () => void;
  onSubmit: (finalReason: string) => Promise<void> | void;
};

const QuitReasonModal: React.FC<QuitReasonModalProps> = ({
  open,
  title,
  question,
  reasons,
  otherPlaceholder,
  stayLabel,
  closeLabel,
  onCancel,
  onSubmit,
}) => {
  const [selectedQuitReason, setSelectedQuitReason] = useState<string | null>(null);
  const [customQuitReason, setCustomQuitReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Reset local state when opening
  useEffect(() => {
    if (open) {
      setSelectedQuitReason(null);
      setCustomQuitReason('');
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    if (!selectedQuitReason || (selectedQuitReason === 'other' && !customQuitReason.trim())) {
      return;
    }
    try {
      setIsSubmitting(true);
      const finalReason = selectedQuitReason === 'other' ? customQuitReason : selectedQuitReason;
      await onSubmit(finalReason);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedQuitReason, customQuitReason, onSubmit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    },
    [onCancel]
  );

  const reasonEntries = useMemo(() => Object.entries(reasons || {}), [reasons]);

  if (!open) return null;

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm"
      onClick={onCancel}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl border"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{question}</p>

        <div className="space-y-6 mb-6">
          {reasonEntries.map(([key, value]) => (
            <label key={key} className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="quitReason"
                value={key}
                checked={selectedQuitReason === key}
                onChange={(e) => setSelectedQuitReason(e.target.value)}
                className="h-4 w-4 accent-black focus:ring-black border-gray-300"
              />
              <span className="text-sm text-gray-700">{String(value)}</span>
            </label>
          ))}
        </div>

        {selectedQuitReason === 'other' && (
          <div className="mb-6">
            <textarea
              name="customQuitReason"
              value={customQuitReason}
              onChange={(e) => {
                // eslint-disable-next-line no-console
                setCustomQuitReason(e.target.value);
              }}
              placeholder={otherPlaceholder}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 max-h-40"
              rows={3}
            />
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            {stayLabel}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedQuitReason || (selectedQuitReason === 'other' && !customQuitReason.trim()) || isSubmitting}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? '...' : closeLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(QuitReasonModal);


